/**
 * Growth-research SSE pipeline → store subscriber.
 * Migrated in Phase 8 commit 75.
 */

import { sseEvents } from "./sseEvents";
import type { CampaignData, CoAgentMessage, StepStatus } from "../store/department/types";

interface GrowthSubscriberHooks {
  addStepLog: (stepIndex: number, text: string) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  setStepState: (
    updater: (state: {
      currentStepIndex: number;
      stepStatuses: Record<number, StepStatus>;
    }) => Partial<{ currentStepIndex: number; stepStatuses: Record<number, StepStatus> }>,
  ) => void;
  setCampaignName: (name: string) => void;
  setFocusedStep: (index: number) => void;
}

let wired = false;

export function wireGrowthSubscriber(getHooks: () => GrowthSubscriberHooks): void {
  if (wired) return;
  wired = true;

  sseEvents.subscribe("growth:session-named", ({ name }) => {
    getHooks().setCampaignName(name);
  });

  sseEvents.subscribe("growth:website-scrape-running", ({ url }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      growthResearchPhase: "website_scrape",
      growthResearchProgress: { phase: "website_scrape", completed: 0, total: 3 },
    });
    hooks.addStepLog(1, `Scraping website: ${url}...`);
  });

  sseEvents.subscribe("growth:website-scrape-complete", ({ data }) => {
    const hooks = getHooks();
    const chars = Number(data.chars ?? 0);
    const title = typeof data.title === "string" ? data.title : "";
    const url = typeof data.url === "string" ? data.url : "";
    hooks.updateCampaignData({
      growthResearchPhase: "site_analysis",
      growthResearchProgress: { phase: "website_scrape", completed: 1, total: 3 },
    });
    hooks.addStepLog(1, `[SUCCESS] Scraped ${chars} chars from ${title || url}`);
  });

  sseEvents.subscribe("growth:website-scrape-skipped", ({ reason }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({
      growthResearchPhase: "site_analysis",
      growthResearchProgress: { phase: "website_scrape", completed: 1, total: 3 },
    });
    hooks.addStepLog(1, `[WARN] Website scrape skipped: ${reason}`);
  });

  sseEvents.subscribe("growth:site-analysis-running", () => {
    getHooks().updateCampaignData({
      growthResearchPhase: "site_analysis",
      growthResearchProgress: { phase: "site_analysis", completed: 1, total: 3 },
    });
  });

  sseEvents.subscribe("growth:site-analysis-complete", ({ data, inputUrl }) => {
    const hooks = getHooks();
    const funnelType = (data.funnelType as string | undefined) || "unknown";
    hooks.addStepLog(1, `[SUCCESS] Site analyzed — funnel type: ${funnelType}`);
    hooks.updateCampaignData({
      growthResearchPhase: "event_extraction",
      growthResearchProgress: { phase: "site_analysis", completed: 2, total: 3 },
      websiteAnalysis: {
        url: inputUrl,
        title: (data.title as string | undefined) || "",
        summary: (data.summary as string | undefined) || "",
        funnelType: (data.funnelType as string | undefined) || "leadgen",
        pages: (data.pages ?? []) as CampaignData["websiteAnalysis"] extends
          | { pages: infer P }
          | undefined
          ? P
          : never,
      } as CampaignData["websiteAnalysis"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 1,
      stepStatuses: { ...state.stepStatuses, 1: "completed", 2: "running" },
    }));
  });

  sseEvents.subscribe("growth:event-extraction-complete", ({ data }) => {
    const hooks = getHooks();
    const count = Number(data.count ?? 0);
    hooks.addStepLog(2, `[SUCCESS] Extracted ${count} tracking events`);
    hooks.updateCampaignData({
      growthResearchPhase: "done",
      growthResearchProgress: { phase: "event_extraction", completed: 3, total: 3 },
      trackingEvents: (data.events ?? []) as CampaignData["trackingEvents"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 2: "waiting" },
    }));
    hooks.setFocusedStep(2);
  });

  sseEvents.subscribe("growth:done", ({ data }) => {
    const hooks = getHooks();
    const trackingEvents = data.tracking_events as unknown[] | undefined;
    const websiteAnalysis = data.website_analysis as
      | { funnelType?: string }
      | undefined;
    hooks.updateCampaignData({
      growthResearchStatus: "complete",
      growthResearchPhase: "done",
      websiteAnalysis: data.website_analysis as CampaignData["websiteAnalysis"],
      trackingEvents: data.tracking_events as CampaignData["trackingEvents"],
      totalCreditsUsed: data.total_credits_used as CampaignData["totalCreditsUsed"],
    });
    hooks.setStepState((state) => ({
      currentStepIndex: 2,
      stepStatuses: { ...state.stepStatuses, 2: "waiting" },
    }));
    hooks.setFocusedStep(2);
    hooks.addMessage({
      role: "agent",
      content: `Website analysis complete! I identified ${
        trackingEvents?.length || 0
      } tracking events for your ${
        websiteAnalysis?.funnelType || ""
      } funnel. Let's review the tracking plan.`,
      widget: "trackingPlan",
    });
  });

  sseEvents.subscribe("growth:error", ({ message }) => {
    const hooks = getHooks();
    hooks.updateCampaignData({ growthResearchStatus: "error", growthResearchPhase: "error" });
    hooks.addStepLog(1, `[ERROR] ${message}`);
    hooks.setStepState((state) => ({
      currentStepIndex: 0,
      stepStatuses: { ...state.stepStatuses, 0: "waiting", 1: "pending" },
    }));
    hooks.addMessage({
      role: "agent",
      content: `Growth research encountered an error: ${message}`,
    });
  });
}
